import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import AnalyticsTracker from '../../components/Analytics/AnalyticsTracker';
import ContactForm from '../../components/ContactForm/ContactForm';
import { trackPhoneCall, trackEmailClick, trackVideoPlay } from '../../components/Analytics/Analytics';
import './KitConsultingPage.css';

const KitConsultingPage = ({ darkMode, toggleDarkMode }) => {
  const handleVideoPlay = () => {
    trackVideoPlay('kit_consulting_video');
  };

  const handlePhoneCall = () => {
    trackPhoneCall('kit_consulting_phone');
  };

  const handleEmailClick = () => {
    trackEmailClick('kit_consulting_email');
  };

  return (
    <div className={`kit-consulting-page ${darkMode ? 'dark' : 'light'}`}>
      <SEO 
        title="Kit Consulting - Consultoría Tecnológica Especializada | E-ASY"
        description="Servicios de consultoría tecnológica especializada para empresas. Transformación digital, optimización de procesos y estrategias tecnológicas personalizadas."
        keywords="consultoría tecnológica, transformación digital, optimización procesos, estrategia tecnológica, consultoría IT, digitalización empresarial"
        canonical="https://e-asy.com/kit-consulting"
      />
      <AnalyticsTracker />
      
      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      {/* Hero Section */}
      <section className="kit-hero">
        <div className="kit-hero-content">
          <div className="kit-hero-text">
            <h1 className="kit-hero-title">
              Kit Consulting
            </h1>
            <p className="kit-hero-subtitle">
              Consultoría tecnológica especializada para transformar tu empresa. 
              Optimizamos procesos, implementamos soluciones digitales y desarrollamos 
              estrategias tecnológicas que impulsan el crecimiento de tu negocio.
            </p>
            <p className="kit-hero-continuidad">
              Si el bono va a migración cloud, Microsoft 365, Azure, Google Workspace
              o Business Central, la continuidad está en nuestra{' '}
              <Link to="/servicios/consultoria-microsoft-azure">
                consultoría Microsoft, Azure y partners
              </Link>
              : el entorno concreto después de la ayuda, para pymes sin equipo IT propio.
            </p>
            <div className="kit-hero-stats">
              <div className="hero-stat">
                <div className="hero-stat-number">500+</div>
                <div className="hero-stat-label">Proyectos Exitosos</div>
              </div>
              <div className="hero-stat">
                <div className="hero-stat-number">98%</div>
                <div className="hero-stat-label">Satisfacción Cliente</div>
              </div>
              <div className="hero-stat">
                <div className="hero-stat-number">24/7</div>
                <div className="hero-stat-label">Soporte Técnico</div>
              </div>
            </div>
          </div>
          <div className="kit-hero-video">
            <div className="kit-hero-visual">
              <video 
                autoPlay 
                muted 
                loop 
                playsInline
                onClick={handleVideoPlay}
                style={{ cursor: 'pointer' }}
              >
                <source src="/videos/consulting-hero.mp4" type="video/mp4" />
                Tu navegador no soporta el elemento video.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Beneficios Section */}
      <section className="beneficios-section">
        <div className="beneficios-content">
          <div className="beneficios-header">
            <h2 className="beneficios-title">¿Por qué elegir Kit Consulting?</h2>
            <p className="beneficios-subtitle">
              Nuestros servicios de consultoría tecnológica están diseñados para 
              maximizar la eficiencia y rentabilidad de tu empresa
            </p>
          </div>
          <div className="beneficios-grid">
            <div className="beneficio-card">
              <div className="beneficio-icon">🎯</div>
              <h3 className="beneficio-title">Estrategia Personalizada</h3>
              <p className="beneficio-description">
                Desarrollamos estrategias tecnológicas adaptadas a las necesidades 
                específicas de tu empresa y sector de actividad.
              </p>
            </div>
            <div className="beneficio-card">
              <div className="beneficio-icon">⚡</div>
              <h3 className="beneficio-title">Optimización de Procesos</h3>
              <p className="beneficio-description">
                Identificamos y optimizamos procesos internos para mejorar 
                la eficiencia operativa y reducir costos.
              </p>
            </div>
            <div className="beneficio-card">
              <div className="beneficio-icon">🚀</div>
              <h3 className="beneficio-title">Transformación Digital</h3>
              <p className="beneficio-description">
                Guiamos tu empresa en su proceso de transformación digital 
                con las mejores tecnologías y metodologías.
              </p>
            </div>
            <div className="beneficio-card">
              <div className="beneficio-icon">📊</div>
              <h3 className="beneficio-title">Análisis de Datos</h3>
              <p className="beneficio-description">
                Implementamos soluciones de Business Intelligence para 
                tomar decisiones basadas en datos reales.
              </p>
            </div>
            <div className="beneficio-card">
              <div className="beneficio-icon">🔒</div>
              <h3 className="beneficio-title">Seguridad Informática</h3>
              <p className="beneficio-description">
                Protegemos tu infraestructura tecnológica con las mejores 
                prácticas de ciberseguridad.
              </p>
            </div>
            <div className="beneficio-card">
              <div className="beneficio-icon">👥</div>
              <h3 className="beneficio-title">Formación de Equipos</h3>
              <p className="beneficio-description">
                Capacitamos a tu equipo en las nuevas tecnologías y 
                metodologías implementadas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Proceso Section */}
      <section className="proceso-section">
        <div className="proceso-content">
          <div className="proceso-header">
            <h2 className="proceso-title">Nuestro Proceso de Consultoría</h2>
            <p className="proceso-subtitle">
              Metodología probada que garantiza resultados medibles y sostenibles
            </p>
          </div>
          <div className="proceso-steps">
            <div className="proceso-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3 className="step-title">Diagnóstico Inicial</h3>
                <p className="step-description">
                  Realizamos un análisis completo de tu empresa, procesos actuales, 
                  infraestructura tecnológica y objetivos de negocio.
                </p>
              </div>
            </div>
            <div className="proceso-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3 className="step-title">Estrategia y Planificación</h3>
                <p className="step-description">
                  Desarrollamos una hoja de ruta personalizada con objetivos claros, 
                  cronogramas y métricas de éxito definidas.
                </p>
              </div>
            </div>
            <div className="proceso-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3 className="step-title">Implementación</h3>
                <p className="step-description">
                  Ejecutamos las soluciones tecnológicas con metodologías ágiles, 
                  asegurando la mínima disrupción en tus operaciones.
                </p>
              </div>
            </div>
            <div className="proceso-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3 className="step-title">Seguimiento y Optimización</h3>
                <p className="step-description">
                  Monitoreamos los resultados, realizamos ajustes necesarios y 
                  proporcionamos soporte continuo para maximizar el ROI.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="kit-main">
        <div className="kit-content">
          {/* Form Section */}
          <section className="kit-form-section">
            <div className="form-header">
              <h2 className="form-title">Solicita tu Consultoría Gratuita</h2>
              <p className="form-subtitle">
                Descubre cómo podemos transformar tu empresa. 
                Agenda una sesión de consultoría sin costo y obtén un análisis 
                detallado de oportunidades de mejora.
              </p>
            </div>
            <ContactForm
              darkMode={darkMode}
              source="kit-consulting"
              trackingEvent="kit_consulting_form"
              pageName="Kit Consulting"
              subjectPrefix="[Kit Consulting]"
              emptyMessageFallback="Solicitud de Kit Consulting desde /solicitud-kit-consulting"
            />
          </section>

          {/* Info Section */}
          <section className="kit-info-section">
            <div className="info-header">
              <h2 className="info-title">Servicios de Consultoría</h2>
              <p className="info-subtitle">
                Ofrecemos servicios especializados en diferentes áreas tecnológicas
              </p>
            </div>
            
            <div className="info-cards">
              <div className="info-card">
                <h3 className="card-title">Consultoría Estratégica</h3>
                <ul className="card-list">
                  <li>Análisis de madurez digital</li>
                  <li>Roadmap de transformación</li>
                  <li>Evaluación de tecnologías</li>
                  <li>Optimización de procesos</li>
                </ul>
              </div>
              <div className="info-card">
                <h3 className="card-title">Consultoría Técnica</h3>
                <ul className="card-list">
                  <li>Arquitectura de sistemas</li>
                  <li>Migración a la nube</li>
                  <li>Integración de sistemas</li>
                  <li>Optimización de rendimiento</li>
                </ul>
              </div>
              <div className="info-card">
                <h3 className="card-title">Consultoría de Datos</h3>
                <ul className="card-list">
                  <li>Estrategia de datos</li>
                  <li>Business Intelligence</li>
                  <li>Analytics avanzado</li>
                  <li>Gobernanza de datos</li>
                </ul>
              </div>
            </div>

            {/* Módulos de Kit Consulting Section */}
            <section className="modulos-section">
              <div className="modulos-header">
                <h2 className="modulos-title">Módulos de Kit Consulting</h2>
                <p className="modulos-subtitle">
                  Servicios especializados de consultoría tecnológica para impulsar tu transformación digital
                </p>
              </div>
              
              <div className="modulos-grid">
                <div className="modulo-card">
                  <div className="modulo-icon">🎯</div>
                  <h3 className="modulo-title">Estrategia Digital</h3>
                  <p className="modulo-description">
                    Desarrollo de estrategias de transformación digital personalizadas para tu empresa.
                  </p>
                  <div className="modulo-details">
                    <span className="modulo-value">Desde €3.000</span>
                    <span className="modulo-duration">2-4 meses</span>
                  </div>
                </div>
                
                <div className="modulo-card">
                  <div className="modulo-icon">⚡</div>
                  <h3 className="modulo-title">Optimización de Procesos</h3>
                  <p className="modulo-description">
                    Análisis y mejora de procesos empresariales para maximizar la eficiencia operativa.
                  </p>
                  <div className="modulo-details">
                    <span className="modulo-value">Desde €2.500</span>
                    <span className="modulo-duration">3-6 meses</span>
                  </div>
                </div>
                
                <div className="modulo-card">
                  <div className="modulo-icon">☁️</div>
                  <h3 className="modulo-title">Migración a la Nube</h3>
                  <p className="modulo-description">
                    Planificación e implementación de estrategias de migración cloud para tu empresa.
                    Microsoft 365, Azure, Google Workspace, Business Central y AWS.
                  </p>
                  <Link
                    to="/servicios/consultoria-microsoft-azure"
                    className="modulo-link"
                  >
                    Ver consultoría Microsoft, Azure y partners
                  </Link>
                  <div className="modulo-details">
                    <span className="modulo-value">Desde €4.000</span>
                    <span className="modulo-duration">4-8 meses</span>
                  </div>
                </div>
                
                <div className="modulo-card">
                  <div className="modulo-icon">📊</div>
                  <h3 className="modulo-title">Business Intelligence</h3>
                  <p className="modulo-description">
                    Implementación de soluciones de análisis de datos y dashboards ejecutivos.
                  </p>
                  <div className="modulo-details">
                    <span className="modulo-value">Desde €3.500</span>
                    <span className="modulo-duration">3-6 meses</span>
                  </div>
                </div>
                
                <div className="modulo-card">
                  <div className="modulo-icon">🔒</div>
                  <h3 className="modulo-title">Ciberseguridad</h3>
                  <p className="modulo-description">
                    Auditorías de seguridad, implementación de políticas y protección de datos.
                  </p>
                  <div className="modulo-details">
                    <span className="modulo-value">Desde €2.500</span>
                    <span className="modulo-duration">2-4 meses</span>
                  </div>
                </div>
                
                <div className="modulo-card">
                  <div className="modulo-icon">🤖</div>
                  <h3 className="modulo-title">Automatización</h3>
                  <p className="modulo-description">
                    Implementación de RPA y automatización de procesos repetitivos.
                  </p>
                  <div className="modulo-details">
                    <span className="modulo-value">Desde €3.000</span>
                    <span className="modulo-duration">3-6 meses</span>
                  </div>
                </div>
                
                <div className="modulo-card">
                  <div className="modulo-icon">📱</div>
                  <h3 className="modulo-title">Transformación Digital</h3>
                  <p className="modulo-description">
                    Guía completa en el proceso de digitalización de tu empresa.
                  </p>
                  <div className="modulo-details">
                    <span className="modulo-value">Desde €5.000</span>
                    <span className="modulo-duration">6-12 meses</span>
                  </div>
                </div>
                
                <div className="modulo-card">
                  <div className="modulo-icon">🔗</div>
                  <h3 className="modulo-title">Integración de Sistemas</h3>
                  <p className="modulo-description">
                    Conectividad entre diferentes sistemas y plataformas empresariales.
                  </p>
                  <div className="modulo-details">
                    <span className="modulo-value">Desde €3.500</span>
                    <span className="modulo-duration">4-8 meses</span>
                  </div>
                </div>
                
                <div className="modulo-card">
                  <div className="modulo-icon">📈</div>
                  <h3 className="modulo-title">Análisis de Datos</h3>
                  <p className="modulo-description">
                    Implementación de soluciones de analytics y reporting avanzado.
                  </p>
                  <div className="modulo-details">
                    <span className="modulo-value">Desde €2.500</span>
                    <span className="modulo-duration">2-4 meses</span>
                  </div>
                </div>
                
                <div className="modulo-card">
                  <div className="modulo-icon">👥</div>
                  <h3 className="modulo-title">Gestión del Cambio</h3>
                  <p className="modulo-description">
                    Formación de equipos y gestión de la adopción de nuevas tecnologías.
                  </p>
                  <div className="modulo-details">
                    <span className="modulo-value">Desde €2.000</span>
                    <span className="modulo-duration">2-4 meses</span>
                  </div>
                </div>
                
                <div className="modulo-card">
                  <div className="modulo-icon">💼</div>
                  <h3 className="modulo-title">Consultoría Financiera IT</h3>
                  <p className="modulo-description">
                    Optimización de costos tecnológicos y análisis de ROI de inversiones IT.
                  </p>
                  <div className="modulo-details">
                    <span className="modulo-value">Desde €3.000</span>
                    <span className="modulo-duration">3-6 meses</span>
                  </div>
                </div>
                
                <div className="modulo-card">
                  <div className="modulo-icon">🎨</div>
                  <h3 className="modulo-title">UX/UI Consulting</h3>
                  <p className="modulo-description">
                    Mejora de la experiencia de usuario y diseño de interfaces digitales.
                  </p>
                  <div className="modulo-details">
                    <span className="modulo-value">Desde €2.500</span>
                    <span className="modulo-duration">2-4 meses</span>
                  </div>
                </div>
              </div>
              
              <div className="modulos-info">
                <div className="modulos-highlight">
                  <h4 className="highlight-title">¿Por qué elegir nuestros módulos?</h4>
                  <p className="highlight-text">
                    Cada módulo está diseñado para abordar necesidades específicas de tu empresa. 
                    Nuestros consultores expertos te guiarán en la selección de los módulos más 
                    adecuados para maximizar el impacto de tu inversión tecnológica.
                  </p>
                  <div className="highlight-features">
                    <div className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span className="feature-text">Consultores certificados</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span className="feature-text">Metodología probada</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span className="feature-text">Resultados medibles</span>
                    </div>
                    <div className="feature-item">
                      <span className="feature-icon">✓</span>
                      <span className="feature-text">Soporte continuo</span>
                    </div>
                  </div>
                </div>
                
                <div className="modulos-requirements">
                  <h4 className="requirements-title">Empresas Elegibles</h4>
                  <ul className="requirements-list">
                    <li>Pymes con 10-49 empleados: hasta €12.000</li>
                    <li>Pymes con 3-9 empleados: hasta €6.000</li>
                    <li>Autónomos: hasta €2.000</li>
                    <li>Microempresas: hasta €2.000</li>
                  </ul>
                </div>
              </div>

              <div className="kit-partners-continuidad">
                <h3 className="kit-partners-continuidad-title">
                  Después del Kit: el entorno cloud
                </h3>
                <p className="kit-partners-continuidad-text">
                  La ayuda cubre consultoría o implantación. Si lo que buscáis es
                  un equipo tecnológico externo —cuentas, infraestructura de
                  proyectos y operación, no solo un alta de Outlook— esa línea
                  está en{' '}
                  <Link to="/servicios/consultoria-microsoft-azure">
                    consultoría Microsoft 365, Azure, Workspace y AWS
                  </Link>
                  . Roadmap de Microsoft Solutions Partner en curso; el trabajo,
                  no el sello.
                </p>
              </div>
            </section>

            <div className="contact-methods">
              <div className="contact-method">
                <div className="method-content">
                  <h4 className="method-title">Teléfono</h4>
                  <div className="method-value">
                    <a href="tel:+34928123456" onClick={handlePhoneCall}>
                      +34 928 123 456
                    </a>
                  </div>
                  <p className="method-description">
                    Lunes a Viernes, 9:00 - 18:00
                  </p>
                </div>
              </div>
              <div className="contact-method">
                <div className="method-content">
                  <h4 className="method-title">Email</h4>
                  <div className="method-value">
                    <a href="mailto:consulting@e-asy.com" onClick={handleEmailClick}>
                      consulting@e-asy.com
                    </a>
                  </div>
                  <p className="method-description">
                    Respuesta en menos de 24 horas
                  </p>
                </div>
              </div>
              <div className="contact-method">
                <div className="method-content">
                  <h4 className="method-title">WhatsApp</h4>
                  <div className="method-value">
                    <a href="https://wa.me/34928123456" target="_blank" rel="noopener noreferrer">
                      +34 928 123 456
                    </a>
                  </div>
                  <p className="method-description">
                    Consultas rápidas y respuestas inmediatas
                  </p>
                </div>
              </div>
            </div>

            <div className="kit-benefits">
              <h3 className="benefits-title">Beneficios de Nuestra Consultoría</h3>
              <div className="benefits-list">
                <div className="benefit-item">
                  <div className="benefit-icon">✓</div>
                  <div className="benefit-text">Reducción de costos operativos hasta 40%</div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">✓</div>
                  <div className="benefit-text">Mejora de la eficiencia productiva</div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">✓</div>
                  <div className="benefit-text">Implementación de mejores prácticas</div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">✓</div>
                  <div className="benefit-text">ROI medible y resultados tangibles</div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">✓</div>
                  <div className="benefit-text">Soporte continuo y seguimiento</div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
    </div>
  );
};

export default KitConsultingPage; 